import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Wallet, BellRing, CheckCircle2, ExternalLink, RefreshCw } from "lucide-react";
import api from "../console/api";

const AUBERGINE = "#3E1F46";
const PLUM = "#5B2A4F";

const FILTERS = [
  { v: "all", label: "All owing" },
  { v: "unpaid", label: "Unpaid" },
  { v: "partial", label: "Partial" },
  { v: "overdue", label: "Overdue 5d+" },
];

export default function AdminPayments() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/console/payments/overview");
      setData(r.data);
    } catch {
      toast.error("Couldn't load payments.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    const all = data?.orders || [];
    if (filter === "unpaid") return all.filter((o) => o.payment_status === "unpaid");
    if (filter === "partial") return all.filter((o) => o.payment_status === "partial");
    if (filter === "overdue") return all.filter((o) => o.days_outstanding >= 5);
    return all;
  }, [data, filter]);

  const remind = async (o) => {
    setBusy(`remind-${o.id}`);
    try {
      const r = await api.post(`/console/orders/${o.id}/email-reminder`, { origin_url: window.location.origin });
      toast.success(`Reminder sent to ${r.data?.sent_to}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send reminder.");
    } finally { setBusy(""); }
  };

  const markPaid = async (o) => {
    if (!window.confirm(`Mark ${o.customer_name || o.email}'s order (NZ$${Number(o.total || 0).toFixed(2)}) as PAID?`)) return;
    setBusy(`paid-${o.id}`);
    try {
      await api.patch(`/console/orders/${o.id}`, { payment_status: "paid" });
      toast.success("Marked as paid.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update.");
    } finally { setBusy(""); }
  };

  const tiles = data?.tiles || {};

  return (
    <div data-testid="admin-payments" className="max-w-5xl space-y-6">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: PLUM }}>✦ Payments</div>
          <h1 className="font-serif text-4xl mt-1 flex items-center gap-3" style={{ color: AUBERGINE }}>
            <Wallet className="w-7 h-7" /> Who owes what
          </h1>
          <p className="text-sm italic text-[#6E5849] mt-1">Every dollar still out there, oldest first. One tap to nudge, one tap to mark paid.</p>
        </div>
        <button type="button" onClick={load} data-testid="payments-refresh" className="h-9 px-3 bg-white border border-[#E0D1C8] rounded text-xs inline-flex items-center gap-1.5" style={{ color: AUBERGINE }}>
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="payments-tiles">
        <Tile label="Total owed" value={`NZ$${Number(tiles.total_owed || 0).toFixed(2)}`} accent="#A33030" testid="tile-owed" />
        <Tile label="Unpaid orders" value={tiles.unpaid_orders ?? 0} testid="tile-count" />
        <Tile label="Overdue 5d+" value={tiles.overdue_5d ?? 0} accent="#C49247" testid="tile-overdue" />
        <Tile label="Collected this week" value={`NZ$${Number(tiles.collected_this_week || 0).toFixed(2)}`} accent="#2F4A2A" testid="tile-collected" />
      </section>

      <div className="flex gap-1.5 flex-wrap" data-testid="payments-filters">
        {FILTERS.map((f) => (
          <button
            key={f.v} type="button" onClick={() => setFilter(f.v)}
            data-testid={`payments-filter-${f.v}`}
            className={`px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold rounded ${
              filter === f.v ? "text-white" : "bg-white border border-[#E0D1C8] text-[#7A6A55]"
            }`}
            style={filter === f.v ? { backgroundColor: AUBERGINE } : {}}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-[#7A6A55] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E0D1C8] rounded p-10 text-center text-[#7A6A55]" data-testid="payments-empty">
          <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
          Nothing owing here. Lovely.
        </div>
      ) : (
        <div className="bg-white border border-[#E0D1C8] rounded-md divide-y divide-[#F0E6D2]">
          {rows.map((o) => (
            <article key={o.id} data-testid={`payment-row-${o.id}`} className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
              <div className="md:col-span-4 min-w-0">
                <div className="font-serif text-[15px] truncate" style={{ color: AUBERGINE }}>{o.customer_name || o.email || "Unknown"}</div>
                <div className="text-xs text-[#7A6A55] truncate">{o.email}</div>
                <Link to={`/admin/order/${o.id}`} className="text-[10px] tracking-[0.25em] uppercase inline-flex items-center gap-1 hover:underline" style={{ color: PLUM }}>
                  Ref {String(o.id).slice(0, 8)} <ExternalLink className="w-2.5 h-2.5" />
                </Link>
              </div>
              <div className="md:col-span-3 text-xs text-[#3C2A1E] space-y-0.5">
                <div>
                  <AgeBadge days={o.days_outstanding} />
                  <span className={`ml-2 text-[10px] tracking-[0.2em] uppercase font-semibold ${o.payment_status === "partial" ? "text-[#C49247]" : "text-[#A33030]"}`}>
                    {o.payment_status}
                  </span>
                </div>
                <div className="text-[#7A6A55]">
                  {o.reminder_last_sent_at
                    ? `Last nudged ${String(o.reminder_last_sent_at).slice(0, 10)}${o.reminder_count ? ` (×${o.reminder_count})` : ""}`
                    : "Never reminded"}
                </div>
              </div>
              <div className="md:col-span-2 font-serif text-xl" style={{ color: AUBERGINE }}>
                NZ${Number(o.total || 0).toFixed(2)}
              </div>
              <div className="md:col-span-3 flex md:justify-end gap-2 flex-wrap">
                <button
                  type="button" disabled={busy === `remind-${o.id}`} onClick={() => remind(o)}
                  data-testid={`payment-remind-${o.id}`}
                  className="h-9 px-3 text-[11px] tracking-[0.15em] uppercase font-semibold rounded text-white inline-flex items-center gap-1.5 disabled:opacity-50"
                  style={{ backgroundColor: AUBERGINE }}
                >
                  {busy === `remind-${o.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />}
                  Remind
                </button>
                <button
                  type="button" disabled={busy === `paid-${o.id}`} onClick={() => markPaid(o)}
                  data-testid={`payment-mark-paid-${o.id}`}
                  className="h-9 px-3 text-[11px] tracking-[0.15em] uppercase font-semibold rounded bg-white border inline-flex items-center gap-1.5 disabled:opacity-50"
                  style={{ borderColor: "#2F4A2A", color: "#2F4A2A" }}
                >
                  {busy === `paid-${o.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  Mark paid
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function AgeBadge({ days }) {
  const tone = days >= 10 ? "#A33030" : days >= 5 ? "#C49247" : "#2F4A2A";
  return (
    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: tone }}>
      {days === 0 ? "today" : `${days}d`}
    </span>
  );
}

function Tile({ label, value, accent, testid }) {
  return (
    <div className="bg-white border border-[#E0D1C8] rounded-md p-4" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase font-semibold" style={{ color: accent || PLUM }}>{label}</div>
      <div className="font-serif text-2xl mt-1" style={{ color: AUBERGINE }}>{value}</div>
    </div>
  );
}
